import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { CustomMetric, CustomMetricPreview } from '@/models/custom-metric.model'
import { injectTyped } from '@/inject'
import { ServiceKeys } from '@/services/symbols'

export const useCustomMetricStore = defineStore('customMetricStore', () => {
  const customMetricService = injectTyped(ServiceKeys.customMetrics)

  // state
  const selectedCustomMetric = ref<CustomMetric | undefined>(undefined)
  const customMetrics = ref<CustomMetric[]>([])
  const customMetricPreview = ref<CustomMetricPreview>()

  // computed
  const selectedCustomMetricId = computed<number | undefined>(() => selectedCustomMetric.value?.id)
  const getCustomMetricById = computed(() => {
    return (id: number | undefined) =>
      customMetrics.value.find((customMetric) => customMetric.id === id)
  })

  // actions

  //Custom Metrics:
  function addNewCustomMetric(newCustomMetric: CustomMetric): void {
    customMetrics.value.push(newCustomMetric)
  }

  function setCustomMetrics(newCustomMetrics: CustomMetric[]) {
    customMetrics.value = newCustomMetrics
  }

  function applyCustomMetric(CustomMetric: CustomMetric | undefined): void {
    selectedCustomMetric.value = CustomMetric
  }

  function findCustomMetricIndex(updatedCustomMetric: CustomMetric): number {
    return customMetrics.value.findIndex(
      (customMetric: CustomMetric) => customMetric.id === updatedCustomMetric.id,
    )
  }

  function $reset() {
    setCustomMetrics([])
    applyCustomMetric(undefined)
  }

  async function loadCustomMetricsFromApi(
    projectId: number,
    routeCustomMetricId?: number,
  ): Promise<void> {
    await customMetricService
      .getCustomMetrics(projectId)
      .then(setCustomMetrics)
      .catch((error: unknown) => Promise.reject(error))

    applyCustomMetric(getCustomMetricById.value(routeCustomMetricId))
  }

  async function createNewCustomMetric(
    selectedProjectId: number,
    newCustomMetric: Omit<CustomMetric, 'id'>,
  ): Promise<CustomMetric> {
    const createdCustomMetric: CustomMetric = await customMetricService.createCustomMetric(
      selectedProjectId,
      newCustomMetric,
    )

    addNewCustomMetric(createdCustomMetric)
    return createdCustomMetric
  }

  async function updateCustomMetric(
    selectedProjectId: number,
    customMetricToUpdate: CustomMetric,
  ): Promise<CustomMetric> {
    const index = findCustomMetricIndex(customMetricToUpdate)

    if (index !== -1) {
      customMetrics.value[index] = await customMetricService.updateCustomMetric(
        selectedProjectId,
        customMetricToUpdate,
      )
      return customMetrics.value[index]
    }
    throw Error("Custom metric is not known and can't be udpated.")
  }

  async function deleteCustomMetric(
    selectedProjectId: number,
    CustomMetricToDelete: CustomMetric,
  ): Promise<void> {
    const deletedCustomMetric = await customMetricService.deleteCustomMetric(
      selectedProjectId,
      CustomMetricToDelete.id,
    )

    customMetrics.value.splice(findCustomMetricIndex(deletedCustomMetric), 1)
  }

  function setCustomMetricPreview(preview: CustomMetricPreview) {
    customMetricPreview.value = preview
  }

  return {
    // state
    selectedCustomMetric,
    customMetrics,
    customMetricPreview,

    // computed
    getCustomMetricById,
    selectedCustomMetricId,

    // actions
    addNewCustomMetric,
    setCustomMetrics,
    applyCustomMetric,
    findCustomMetricIndex,
    $reset,
    loadCustomMetricsFromApi,
    createNewCustomMetric,
    updateCustomMetric,
    deleteCustomMetric,
    setCustomMetricPreview,
  }
})
